import {Box, Typography} from '@mui/material';
import './ExitJobModal.scss';
import {t} from 'i18next';
import ArrModal from '../../../../../common/arr-modal/ArrModal';
import {useNavigate} from 'react-router-dom';
import ArrButton from '../../../../../common/arr-button/ArrButton';
import './ExitJobModal.scss';
import {useDispatch, useSelector, useStore} from 'react-redux';
import {RootState} from '../../../../../../redux/store';
import {
  resetEditMode,
  resetFormData,
  resetProtocol,
  setCloneJobName,
  setDefineCriteria,
  initializeJobCreateProtocol,
  showToast,
  syncProtocolToFormData,
} from '../../../../../../redux/slice/aiReviewSliceV2';
import {
  useCreateAIReviewJob,
  useUpdateAIReviewJob,
} from '../../../../../../hooks/ai/review/useReview';
import {ToastType} from '../../../../../../shared/constants';
import {CRITERIA} from '../issue-coding-type-header/IssueCodingTypeHeader';
import {AI_REVIEW_JOB_STATUS} from '../../../constant/AiReviewConstants';
import {
  buildCreateJobPayload,
  buildUpdateJobPayload,
} from '../buildCreateJobPayload';

interface ExitJobModalProps {
  open: boolean;
  onClose: () => void;
}

const ExitJobModal = ({open, onClose}: ExitJobModalProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const store = useStore();
  const formData = useSelector((state: RootState) => state.aiReviewV2.formData);
  const {mutate: createAIReviewJob} = useCreateAIReviewJob();
  const {mutate: updateAIReviewJob} = useUpdateAIReviewJob();

  const handleConfirm = () => {
    navigate('/AI/Review');
    dispatch(setCloneJobName(''));
    dispatch(resetFormData());
    dispatch(resetProtocol());
    dispatch(initializeJobCreateProtocol());
    dispatch(resetEditMode());
    dispatch(setDefineCriteria(CRITERIA.MANUAL));

    onClose();
  };

  const triggerToast = () => {
    dispatch(
      showToast({
        title: t('ai.review.newJobDetail.draftToastTitle'),
        message: t('ai.review.newJobDetail.draftToastDesc', {
          value: formData?.jobDetail?.jobName,
        }),
        status: [ToastType.Success],
      })
    );
    navigate('/AI/Review');
    dispatch(setCloneJobName(''));
    dispatch(resetFormData());
    dispatch(resetProtocol());
    dispatch(initializeJobCreateProtocol());
    dispatch(resetEditMode());
    dispatch(setDefineCriteria(CRITERIA.MANUAL));
  };

  const handleDraft = () => {
    onClose();
    dispatch(syncProtocolToFormData());
    const state = store.getState() as RootState;
    const payload = buildCreateJobPayload(
      state.aiReviewV2.formData,
      state.aiReviewV2.jobType,
      AI_REVIEW_JOB_STATUS.draft,
      state.aiReviewV2.protocol,
      state.aiReviewV2.initialProtocol
    );

    const mutationOptions = {
      onSuccess: (data: {errorMessage?: string}) => {
        if (data?.errorMessage) {
          dispatch(
            showToast({
              title: t('common.error'),
              message: data.errorMessage,
              status: [ToastType.Error],
            })
          );
          return;
        }
        triggerToast();
      },
      onError: (error: {
        response?: {data?: {detail?: string}};
        message?: string;
      }) => {
        dispatch(
          showToast({
            title: t('common.error'),
            message:
              error?.response?.data?.detail ??
              error?.message ??
              t('common.error'),
            status: [ToastType.Error],
          })
        );
      },
    };

    if (state.aiReviewV2.formData.jobId) {
      updateAIReviewJob(
        buildUpdateJobPayload(
          state.aiReviewV2.formData,
          state.aiReviewV2.jobType,
          AI_REVIEW_JOB_STATUS.draft,
          state.aiReviewV2.protocol,
          state.aiReviewV2.initialProtocol
        ),
        mutationOptions
      );
      return;
    }

    createAIReviewJob(payload, mutationOptions);
  };

  return (
    <>
      <ArrModal
        className="exitjobdetail"
        showModal={open}
        onClose={onClose}
        showClose={false}
        title={t('ai.review.newJobDetail.exitJobModalTitle')}
        disableFocusTitle
        dataTestId="arr-review-exit-job-detail-modal"
        buttons={[
          {
            id: 'draft-btn',
            label: t('ai.review.newJobDetail.exitJobModalDraftBtn'),
            onClick: handleDraft,
            disabled: !formData?.jobDetail?.jobName?.trim(),
            variant: 'text',
            dataTestId: 'ejd-draft-button',
            className: 'draft-button',
          },
        ]}
      >
        <Typography
          className="exitjobdetail-description"
          id="arr-review-exit-job-detail-modal-description"
          data-testid={`exit-job-modal-exit-job-modal-description`}
        >
          {t('ai.review.newJobDetail.exitJobModalDescription')}
        </Typography>
        <Box className="exit-modal-btn-container">
          <ArrButton
            variant="secondary"
            size="large"
            disabled={false}
            aria-label={t('ai.review.newJobDetail.exitJobModalDiscardBtn')}
            dataTestId="discard-btn"
            className="discard-btn"
            onClick={handleConfirm}
          >
            {t('ai.review.newJobDetail.exitJobModalDiscardBtn')}
          </ArrButton>
          <ArrButton
            variant="primary"
            size="large"
            disabled={false}
            aria-label={t('ai.review.newJobDetail.exitJobModalEditBtn')}
            dataTestId="edit-btn"
            className="edit-btn"
            onClick={onClose}
          >
            {t('ai.review.newJobDetail.exitJobModalEditBtn')}
          </ArrButton>
        </Box>
      </ArrModal>
    </>
  );
};

export default ExitJobModal;
